import React from "react";
import { Brain, Wrench, Zap } from "lucide-react";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";

const MEMORY_TYPE_LABELS: Record<string, string> = {
  preference: "偏好",
  style: "风格",
  intent: "意图",
  habit: "习惯",
  writing_preference: "写作偏好",
  writing_process: "写作过程",
  language_style: "语言风格",
  special: "特殊",
};

const SCOPE_LABELS: Record<string, string> = {
  user: "用户级别",
  workspace: "项目级别",
};

function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen) + "..." : text;
}

const MemoryWriteSummary: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const memoryType = String(args.memory_type || "");
  const typeLabel = MEMORY_TYPE_LABELS[memoryType];
  const scope = typeof args.scope === "string" ? SCOPE_LABELS[args.scope] : null;
  const content = typeof args.content === "string" ? args.content : "";

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {typeLabel && (
        <span className="inline-flex items-center rounded-full bg-pink-100 px-2 py-0.5 text-pgray-700">
          {typeLabel}
        </span>
      )}
      {scope && (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
          {scope}
        </span>
      )}
      {content && (
        <span className="truncate max-w-[200px]">{truncate(content, 50)}</span>
      )}
      {content.length > 0 && <span>{content.length.toLocaleString()} 字</span>}
    </div>
  );
};

const GetToolSummary: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const toolName = String(args.tool_name || args.name || "");

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {toolName && <span className="text-foreground font-medium">{toolName}</span>}
    </div>
  );
};

const GetSkillSummary: React.FC<{ args: Record<string, unknown> }> = ({ args }) => {
  const skillName = String(args.skill_name || args.name || "");

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {skillName && <span className="text-foreground font-medium">{skillName}</span>}
    </div>
  );
};

export const UtilityCard: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const cardStatus: CardStatus = toolCall.status || "detected";
  const toolName = toolCall.toolName;
  const args = toolCall.args || {};

  if (toolName === "memory_write") {
    return (
      <ToolCardShell
        toolName="存储记忆"
        status={cardStatus}
        icon={<Brain className="h-4 w-4" />}
        iconColor="text-pink-500"
        defaultExpanded={false}
      >
        <MemoryWriteSummary args={args} />
        {toolCall.args && Object.keys(toolCall.args).length > 0 && (
          <ToolInput data={toolCall.args} />
        )}
        {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
          <ToolOutput data={toolCall.result} error={toolCall.error} />
        ) : null}
      </ToolCardShell>
    );
  }

  if (toolName === "get_skill") {
    return (
      <ToolCardShell
        toolName="加载技能"
        status={cardStatus}
        icon={<Zap className="h-4 w-4" />}
        iconColor="text-yellow-500"
        defaultExpanded={false}
      >
        <GetSkillSummary args={args} />
        {toolCall.args && Object.keys(toolCall.args).length > 0 && (
          <ToolInput data={toolCall.args} />
        )}
        {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
          <ToolOutput data={toolCall.result} error={toolCall.error} />
        ) : null}
      </ToolCardShell>
    );
  }

  return (
    <ToolCardShell
      toolName="加载工具定义"
      status={cardStatus}
      icon={<Wrench className="h-4 w-4" />}
      iconColor="text-gray-500"
      defaultExpanded={false}
    >
      <GetToolSummary args={args} />
      {toolCall.args && Object.keys(toolCall.args).length > 0 && (
        <ToolInput data={toolCall.args} />
      )}
      {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
        <ToolOutput data={toolCall.result} error={toolCall.error} />
      ) : null}
    </ToolCardShell>
  );
};
